// =====================================================================
// Faz I (2026-05-06) — NPS Dashboard
// =====================================================================
// Net Promoter Score özet sayfası. Backend endpoint:
//   GET /api/nps/admin/summary
//
// Response shape:
//   {
//     nps_score: number | null,           // -100 ile +100 arası
//     total_responses: number,            // son 90 gün
//     breakdown: [{category, count, avg_score}],
//     recent_detractors: [{score, feedback, responded_at, order_id, user_name}]
//   }
//
// NPS Skor formülü: %promoter (9-10) − %detractor (0-6). Pasifler (7-8)
// hesaba katılmaz ama toplamda paydadır. Standart endüstri eşiği:
//   < 0  : kötü
//   0-30 : orta
//   30-50: iyi
//   50+  : mükemmel
// Renkleri buna göre koyuyoruz.
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, LinearProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentNeutralIcon from "@mui/icons-material/SentimentNeutral";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import StarIcon from "@mui/icons-material/Star";
import API from "../services/api";

const fmtDateTime = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

// NPS skoru → renk + etiket (endüstri standardı eşikleri)
const npsTier = (score) => {
  if (score == null) return { color: "#94a3b8", label: "Veri Yok" };
  if (score >= 50)   return { color: "#10b981", label: "Mükemmel" };
  if (score >= 30)   return { color: "#3b82f6", label: "İyi" };
  if (score >= 0)    return { color: "#f59e0b", label: "Orta" };
  return                    { color: "#ef4444", label: "Kötü" };
};

// Kategori → renk + ikon (mail HTML ile aynı renk kodları)
const catMeta = {
  promoter:  { color: "#10b981", label: "Promoter (9-10)",  icon: <SentimentSatisfiedAltIcon /> },
  passive:   { color: "#f59e0b", label: "Pasif (7-8)",      icon: <SentimentNeutralIcon /> },
  detractor: { color: "#ef4444", label: "Detractor (0-6)",  icon: <SentimentVeryDissatisfiedIcon /> }
};

export default function NpsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    API.get("/api/nps/admin/summary")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const tier = npsTier(data.nps_score);
  const total = data.total_responses || 0;

  // Breakdown'da olmayan kategoriyi 0 ile doldur (tutarlı görsel için)
  const bd = ["promoter", "passive", "detractor"].map(cat => {
    const row = data.breakdown.find(b => b.category === cat);
    return {
      category: cat,
      count: row ? Number(row.count) : 0,
      avg: row ? Number(row.avg_score) : null,
      pct: total > 0 && row ? Math.round((row.count / total) * 100) : 0
    };
  });

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Hero başlık */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>NPS Skoru</Typography>
          <Typography variant="body2" color="text.secondary">
            Son 90 gün — Net Promoter Score (müşteri tavsiye eğilimi)
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {/* Ana NPS skoru kartı — büyük, renkli, etiketli */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            p: 3, borderRadius: 3, textAlign: "center",
            background: `linear-gradient(135deg, ${tier.color}22 0%, ${tier.color}11 100%)`,
            border: `2px solid ${tier.color}`, height: "100%"
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
              NPS SKORU
            </Typography>
            <Typography variant="h2" fontWeight={900} sx={{ color: tier.color, my: 1 }}>
              {data.nps_score != null ? (data.nps_score > 0 ? `+${data.nps_score}` : data.nps_score) : "—"}
            </Typography>
            <Chip
              label={tier.label}
              sx={{ bgcolor: tier.color, color: "white", fontWeight: 700, fontSize: 13 }}
            />
            <Typography variant="caption" display="block" mt={2} color="text.secondary">
              Formül: %Promoter − %Detractor (toplam yanıt: {total})
            </Typography>
          </Card>
        </Grid>

        {/* Kategori breakdown — 3 satır promoter/passive/detractor */}
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2.5, borderRadius: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={700} mb={2}>Yanıt Dağılımı</Typography>
            {bd.map(row => {
              const meta = catMeta[row.category];
              return (
                <Box key={row.category} sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: meta.color }}>{meta.icon}</Box>
                    <Typography fontWeight={600}>{meta.label}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {row.count} kişi · %{row.pct}
                    </Typography>
                    {row.avg != null && (
                      <Chip
                        size="small"
                        icon={<StarIcon sx={{ fontSize: "16px !important" }} />}
                        label={`Ort: ${row.avg.toFixed(1)}`}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={row.pct}
                    sx={{
                      height: 10, borderRadius: 5, bgcolor: "#f1f5f9",
                      "& .MuiLinearProgress-bar": { bgcolor: meta.color, borderRadius: 5 }
                    }}
                  />
                </Box>
              );
            })}
          </Card>
        </Grid>

        {/* Detractor yorumları — öğrenmek için en kıymetli geri bildirim */}
        <Grid item xs={12}>
          <Card sx={{ p: 2.5, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SentimentVeryDissatisfiedIcon sx={{ color: "#ef4444" }} />
              <Typography variant="h6" fontWeight={700}>
                Son Detractor Yorumları (Öğrenme Fırsatı)
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip
                label={`${data.recent_detractors?.length || 0} yorum`}
                size="small"
                sx={{ bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 700 }}
              />
            </Box>
            {(!data.recent_detractors || data.recent_detractors.length === 0) ? (
              <Alert severity="success">
                Son 90 günde detractor yorumu yok. Tebrikler! 🎉
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={70}>Skor</TableCell>
                    <TableCell width={140}>Müşteri</TableCell>
                    <TableCell width={100}>Sipariş</TableCell>
                    <TableCell>Yorum</TableCell>
                    <TableCell width={140}>Tarih</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recent_detractors.map((d, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Chip
                          label={`${d.score}/10`}
                          size="small"
                          sx={{ bgcolor: "#ef4444", color: "white", fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>{d.user_name || "—"}</TableCell>
                      <TableCell>
                        <Chip label={`#${d.order_id}`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
                        {d.feedback || <em style={{ color: "#94a3b8" }}>(yorum yok)</em>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {fmtDateTime(d.responded_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
